// CrazyMaze.jsx - Cleaned and final version

import React, { useEffect, useRef, useState, forwardRef } from "react";
import Phaser from "phaser";
import CrazyMazeScene from "./CrazyMazeScene";
import CompletionModal from "./CompletionModal";
import MysteryBoxModal from "./MysteryBoxModal";
import { resetMysteryTracker } from "./mysteryOutcomes";
import TouchControls from "./TouchControls";


const isTouchDevice =
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0) &&
  window.innerWidth <= 1024;


const CrazyMaze = forwardRef(function CrazyMaze(
  { playerEmoji, difficulty = "medium", onScoreChange, onGameOver, onStepUpdate, onGemUpdate, sceneRef },
  ref
) {
  const containerRef = useRef(null);
  const gameRef = useRef(null);
  const cbRef = useRef({});

  const [showModal, setShowModal] = useState(false);
  const [scoreStats, setScoreStats] = useState(null);
  const [gameDimensions, setGameDimensions] = useState({ width: 525, height: 525 });
  const [showMysteryBox, setShowMysteryBox] = useState(false);
  const [pendingMysteryEffect, setPendingMysteryEffect] = useState(null);

  const calculateGameSize = () => {
    const customSettings = JSON.parse(localStorage.getItem("maze-settings") || "{}");
    const cellSize = 25;

    const difficultyDefaults = {
      easy: { rows: 15, cols: 15 },
      medium: { rows: 21, cols: 21 },
      hard: { rows: 31, cols: 31 }
    };

    const defaults = difficultyDefaults[difficulty] || difficultyDefaults.medium;

    let rows = customSettings.rows ? Number(customSettings.rows) : defaults.rows;
    let cols = customSettings.columns ? Number(customSettings.columns) : defaults.cols;

    const headerHeight = 120;
    const footerHeight = 100;
    const sideMargin = 50;

    const availableWidth = window.innerWidth - sideMargin * 2;
    const availableHeight = window.innerHeight - headerHeight - footerHeight;

    const maxCols = Math.floor(availableWidth / cellSize) - 2;
    const maxRows = Math.floor(availableHeight / cellSize) - 2;

    rows = Math.min(rows, maxRows);
    cols = Math.min(cols, maxCols);

    if (rows % 2 === 0) rows--;
    if (cols % 2 === 0) cols--;

    rows = Math.max(15, rows);
    cols = Math.max(15, cols);

    return { width: cols * cellSize, height: rows * cellSize, cellSize, rows, cols };
  };

  useEffect(() => {
    cbRef.current.onScoreChange = onScoreChange;
    cbRef.current.onStepUpdate = onStepUpdate;
    cbRef.current.onGemUpdate = onGemUpdate;

    cbRef.current.onGameOver = (data) => {
      if (!data || !data.gemsCollected) return;
      setScoreStats(data);
      setShowModal(true);
      onGameOver?.(data);
    };

    cbRef.current.onMysteryBoxTriggered = (data) => {
      
      setPendingMysteryEffect(data);
      setShowMysteryBox(true);
    };
  }, []);

  useEffect(() => {
    const { width, height, cellSize } = calculateGameSize();
    setGameDimensions({ width, height });
    resetMysteryTracker();

    const checkAndInit = () => {
      if (!containerRef.current || gameRef.current) return;

      gameRef.current = new Phaser.Game({
        type: Phaser.AUTO,
        width,
        height,
        parent: containerRef.current,
        backgroundColor: "#ffffff",
        physics: { default: "arcade", arcade: { debug: false } },
        audio: {
  disableWebAudio: false,
  noAudio: false
},

        scale: { mode: Phaser.Scale.NONE, width, height },
        scene: class extends CrazyMazeScene {
          constructor() {
            super({ difficulty: difficulty || "medium" }, cellSize);
            this.playerEmoji = playerEmoji;
            this.externalEvents = {
              emit: (evt, data, extraData) => {
               
                if (evt === "scoreChanged") cbRef.current.onScoreChange?.(data);
                if (evt === "gameOver") cbRef.current.onGameOver?.(data);
                if (evt === "stepUpdate") cbRef.current.onStepUpdate?.(data);
                if (evt === "gemUpdate") cbRef.current.onGemUpdate?.(data, extraData);

                if (evt === "mysteryBoxTriggered") cbRef.current.onMysteryBoxTriggered?.(data);
              }
            };
          }
        }
      });

      

      // 🩹 Fix: Resume Web Audio only after user interaction
window.addEventListener("click", () => {
  try {
    const ctx = gameRef.current?.sound?.context;
    if (ctx && ctx.state !== "running" && ctx.state !== "closed") {
      ctx.resume();
    }
  } catch (err) {
    console.warn("AudioContext resume blocked:", err);
  }
}, { once: true });


      // Attach sceneRef once ready
      setTimeout(() => {
        if (sceneRef && gameRef.current?.scene?.keys?.CrazyMazeScene) {
          sceneRef.current = gameRef.current.scene.keys.CrazyMazeScene;
          
        }
      }, 100);
    };

    const interval = setInterval(() => {
      checkAndInit();
      if (gameRef.current) clearInterval(interval);
    }, 50);

    return () => {
      clearInterval(interval);
      gameRef.current?.destroy(true);
      gameRef.current = null;
    };
  }, [difficulty, playerEmoji]);

  return (
    <>
      <div className="flex flex-col items-center justify-center w-full py-4">
        <div
          ref={containerRef}
          className="game-container border-2 border-gray-400 rounded-lg shadow-lg bg-white overflow-hidden"
          style={{ width: `${gameDimensions.width}px`, height: `${gameDimensions.height}px` }}
        />
      </div>

      {showModal && scoreStats && (
        <CompletionModal
          show={showModal}
          onClose={() => setShowModal(false)}
          stats={scoreStats}
        />
      )}

      {showMysteryBox && pendingMysteryEffect && (
        <MysteryBoxModal
          show={showMysteryBox}
          box={pendingMysteryEffect}
          onResult={(outcome) => {
            
            sceneRef.current?.applyMysteryEffect(outcome); // ✅ Directly apply
            setShowMysteryBox(false); // ✅ Hide modal
          }}
          onClose={() => {
            sceneRef.current?.scene.resume();
            sceneRef.current.mysteryBoxActive = false;  // resume if ignored
            setShowMysteryBox(false);
          }}
        />
      )}
      {isTouchDevice && (
  <TouchControls
    onMove={(dir) => {
      if (sceneRef.current?.movePlayer) {
        sceneRef.current.movePlayer(dir);
      }
    }}
  />
)}


 </>
  );
});

export default CrazyMaze;
